import React, { useRef, useState } from 'react'
import LoadingScreen from '../../loadingScreenComp/LoadingScreen';
import { InputGroupPropsTypes } from '../../inputComp/InputGroupType';
import { DepartmentIcon, ForecastIcon, FormIconDataPicker, ModelIcon, NameIcon, SensorIcon } from '../../iconSet/IconSet';
import InputGroup from '../../inputComp/InputGroup';
import { SelectItemTypes } from '../../../CommonTypes';
import StormSurgeViewForecastMap from './StormSurgeViewForecastMap';
import StormSurgeViewSimulationMap from './StormSurgeViewSimulationMap';
import StormSurgeSimulationDataTable from './StormSurgeSimulationDataTable';

type TabTypes = "simulation" | "forecast" | "analyze_impact" | "generate_early_warning"

export type GenerateEarlyWarningDataRow = {
    buildingId: number;
    type: string;
    owner: string;
    mobile: string;
    areaOfRisk: string;
    maxDepth: number;
    startTime: string;
    peakTime: string;
}

type CheckboxType = {
    label: string, 
    ref: React.MutableRefObject<HTMLInputElement | null>, 
    value: string
}

const StormSurgeSimulation = () => {
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const tabs: string[] = ["simulation", "forecast", "analyze_impact", "generate_early_warning"];
    const [selectedTab, setSelectedTab] = useState<TabTypes>("simulation");

    const handleTabClick = (tab: string) => {
        setSelectedTab(tab as TabTypes)
    };

    const simulationNameRef = useRef<HTMLInputElement | null>(null);
    const floodModelTypeRef = useRef<HTMLSelectElement | null>(null);
    const forecastRef = useRef<HTMLSelectElement | null>(null);

    const [floodModelType, setFloodModelType] = useState<SelectItemTypes[]>([
        { name: "Anuga", value: "Anuga" },
        { name: "Hecras", value: "Hecras" },
        { name: "CMOD", value: "CMOD" },
    ])

    const [forecastType, setForecastType] = useState<SelectItemTypes[]>([
        { name: 'ECMWF', value: 'ECMWF' },
        { name: 'GWF', value: 'GWF' },
    ])

    const simulationFormData: InputGroupPropsTypes[] = [
        {
            type: "text",
            icon: <NameIcon width="1.5rem" color="var(--color-main-white)" />,
            color: "var(--color-accent-bg)",
            placeholder: "Enter simulation name",
            label: "Simulation Name",
            name: "simulation_name",
            componentInputElement: simulationNameRef,
        },
        {
            type: "select",
            icon: <ModelIcon width="1.5rem" color="var(--color-main-white)" />,
            color: "var(--color-accent-bg)",
            placeholder: "Select model type",
            label: "Flood Model Type",
            name: "flood_model_type",
            componentSelectElement: floodModelTypeRef,
            optionList: floodModelType
        },
        {
            type: "select",
            icon: <ForecastIcon width="1.5rem" color="var(--color-main-white)" />,
            color: "var(--color-accent-bg)",
            placeholder: "Select forecast",
            label: "Forecast",
            name: "forecast",
            componentSelectElement: forecastRef,
            optionList: forecastType
        }
    ]

    const [floodCusRiverSensorType, setFloodCusRiverSensorType] = useState<SelectItemTypes[]>([
        { name: 'Irrigation Department', value: 'Irrigation Department' },
        { name: 'NBRO Department', value: 'NBRO Department' },
        { name: 'Community', value: 'Community' },
    ])

    const floodCusRiverSensorRef = useRef<HTMLSelectElement | null>(null);

    const simulationFormCustomData: InputGroupPropsTypes[] = [
        {
            type: "select",
            icon: <SensorIcon width="1.5rem" color="var(--color-main-white)" />,
            color: "var(--color-accent-bg)",
            placeholder: "Select river sensor",
            label: "River Sensor",
            name: "river_sensor",
            componentSelectElement: floodCusRiverSensorRef,
            optionList: floodCusRiverSensorType
        }
    ]

    // Forecast tab
    const showAttachmentAreaRef = useRef<HTMLInputElement | null>(null)
    const publishRef = useRef<HTMLInputElement | null>(null)

    const [buildingDataFrom, setBuildingDataFrom] = useState<SelectItemTypes[]>([
        { name: "Irrigation Department", value: "Irrigation Department" },
        { name: "NBRO Department", value: "NBRO Department" },
        { name: "Met Office", value: "Met Office" },
        { name: "Community", value: "Community" },
    ])

    const buildingDataFromRef = useRef<HTMLSelectElement | null>(null);
    
    const buildingIdCheckRef = useRef<HTMLInputElement | null>(null);
    const buildingOwnerNameCheckRef = useRef<HTMLInputElement | null>(null);
    const mobileNumberCheckRef = useRef<HTMLInputElement | null>(null);
    const buildingTypeCheckRef = useRef<HTMLInputElement | null>(null);
    const maxDepthCheckRef = useRef<HTMLInputElement | null>(null);
    const startTimeCheckRef = useRef<HTMLInputElement | null>(null);
    const peakTypeCheckRef = useRef<HTMLInputElement | null>(null);

    const checkboxDataSet: CheckboxType[] = [
        {label: 'Building Id', ref: buildingIdCheckRef, value: 'building_id'},
        {label: 'Building owner name', ref: buildingOwnerNameCheckRef, value: 'building_owner_name'},
        {label: 'Mobile number', ref: mobileNumberCheckRef, value: 'mobile_number'},
        {label: 'Building type', ref: buildingTypeCheckRef, value: 'building_type'},
        {label: 'Maximum depth', ref: maxDepthCheckRef, value: 'maximum_depth'},
        {label: 'Start time', ref: startTimeCheckRef, value: 'start_time'},
        {label: 'Peak type', ref: peakTypeCheckRef, value: 'peak_type'},
    ]

    const formData: InputGroupPropsTypes[] = [
        {
            type: 'select',
            icon: <FormIconDataPicker width="1.5rem" color="var(--color-main-white)" />,
            color: 'var(--color-accent-bg)',
            label: 'Building data from',
            name: 'building_data_from',
            required: false,
            componentSelectElement: buildingDataFromRef,
            placeholder: 'Not selected',
            optionList: floodCusRiverSensorType
        }
    ]

    // Generate early warning tab
    const [selectedGenerateEarlyWarning, setSelectedGenerateEarlyWarning] = useState<GenerateEarlyWarningDataRow[]>([]);

    const handleSimulationTabNextButtonClick = () => {
        setIsLoading(true);
      
        setTimeout(() => {
            setIsLoading(false);
            setSelectedTab("forecast");
        }, 2000); // 0.5 seconds
    }

    // Handle forecast tab next button click
    const handleForecastTabNextButtonClick = () => {
        console.log("forecast tab next button clicked");
        setSelectedTab('analyze_impact');
    };

    // Handle analyze impact tab next button click
    const handleAnalyzeImpactTabNextButtonClick = () => {
        setSelectedTab("generate_early_warning");
    };

    // Handle generate ew tab next button click
    const handleGenerateEWTabNextButtonClick = () => {
        console.log("generate ew tab trigger button clicked");
    };
    
    return (
        <div className={`container-wrapper`} style={{maxWidth: '120rem'}}>
			<span className={`title`}>Storm Surge</span>
			<div className="tab-area">
				{tabs.map((tab, index) => (
					<span key={index} style={{ borderStyle: selectedTab === tab ? "solid" : "none" }} onClick={() => handleTabClick(tab)}>
						{tab.replace(/_/g, " ")}
					</span>
				))}
			</div>

            {selectedTab === 'simulation' && (
                <div className='flood-simulation-wrapper'>
                    <div className='cell form-wrapper'>
                        {simulationFormData.map((field, index) => (
                            <InputGroup key={index} {...field} />
                        ))}

                        <div className='flood-simulation-custom-value-wrapper'>
                        <span className='title'>Custom Values</span>
                            {simulationFormCustomData.map((field, index) => (
                                <InputGroup key={index} {...field} />
                            ))}
                        </div>
                        
                    </div>
                    <div className='cell map-wrapper'>
                        <StormSurgeViewSimulationMap />
                    </div>
                    <div className='cell button-wrapper'>
                        <button onClick={(e) => handleSimulationTabNextButtonClick()} onTouchStart={(e) => handleSimulationTabNextButtonClick()}>Run</button>
                    </div>
                </div>
            )}
            
            {selectedTab === 'forecast' && (
                <div className='flood-forecast-wrapper'>
                    <div className='map-wrapper'>
                        <StormSurgeViewForecastMap />
                    </div>
                    <div className='button-wrapper'>
                        <div className='checkbox-wrapper'>
                            <input className='check' type="checkbox" id="check-show-attachment-area" ref={showAttachmentAreaRef} />
                            <label htmlFor="check-show-attachment-area">Show attachment area</label>
                        </div>
                        <div className='checkbox-wrapper'>
                            <input className='check' type="checkbox" id="check-publish" ref={publishRef} />
                            <label htmlFor="check-publish">Publish</label>
                        </div>
                        <button onClick={(e) => handleForecastTabNextButtonClick()} onTouchStart={(e) => handleForecastTabNextButtonClick()}>Next</button>
                    </div>
                </div>
            )}

            {selectedTab === 'analyze_impact' && (
                <div className='analyze-impact-wrapper'>
                    {formData.map((field, index) => (
                        <InputGroup key={index} {...field}/>
                    ))}

                    <div className='check-analyze-impact'>
                        <span className='title'>Select data</span>
                        <div className='data-body'>
                            {checkboxDataSet.map((item, index) => (
                                <div className='cell' key={index}>
                                    <input type="checkbox" id={item.value} ref={item.ref}/>
                                    <label htmlFor={item.value}>{item.label}</label>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className='button-wrapper'>
                        <button onClick={(e) => handleAnalyzeImpactTabNextButtonClick()} onTouchStart={(e) => handleAnalyzeImpactTabNextButtonClick()}>Next</button>
                    </div>
                </div>
            )}

            {selectedTab === 'generate_early_warning' && (
                <div className='generate-early-warning-wrapper'>
                    <StormSurgeSimulationDataTable ewData={selectedGenerateEarlyWarning} setSelectedEWData={setSelectedGenerateEarlyWarning}/>
                    <div className='button-wrapper'>
                        <button onClick={(e) => handleGenerateEWTabNextButtonClick()} onTouchStart={(e) => handleGenerateEWTabNextButtonClick()}>Trigger</button>
                    </div>
                </div>
            )}

			{isLoading ? <LoadingScreen /> : null}
		</div>
    )
}

export default StormSurgeSimulation